import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction } from "react"
import { PencilSquareIcon } from "@heroicons/react/24/outline"
import { Table } from "@/components"
import { SpotRatesModifierType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { data: SpotRatesModifierType[] }
  header: {
    name: string
    width: string
  }[]
  handleFormUpdate: (form: SpotRatesModifierType) => void
}

export const SpotRatesModifier: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<SpotRatesModifierType>()

  const onSubmit = (data: SpotRatesModifierType) => {
    data.days = parseInt(String(data.days))
    data.spotRateWeight = parseFloat(String(data.spotRateWeight))
    data.directRateWeight = parseFloat(String(data.directRateWeight))
    data.proximityMiles = parseFloat(String(data.proximityMiles))
    props.handleFormUpdate(data)
  }

  const populateData = (e: SpotRatesModifierType) => {
    setValue("id", e.id)
    setValue("days", e.days)
    setValue("spotRateWeight", e.spotRateWeight)
    setValue("directRateWeight", e.directRateWeight)
    setValue("proximityMiles", e.proximityMiles ?? 100)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      days: undefined,
      spotRateWeight: undefined,
      directRateWeight: undefined,
      proximityMiles: undefined,
      id: undefined,
    })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="small"
        title="Update Spot Rates Modifier"
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">
                Days
              </label>
              <input
                className={
                  (errors.days ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Days"
                {...register("days", { required: true })}
              />
            </fieldset>

            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  Spot Rate Weight
                </label>
                <input
                  className={
                    (errors.spotRateWeight
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter Spot Rate Weight"
                  {...register("spotRateWeight", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  Direct Rate Weight
                </label>
                <input
                  className={
                    (errors.directRateWeight
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter Direct Rate Weight"
                  {...register("directRateWeight", { required: true })}
                />
              </fieldset>
            </div>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">
                Proximity Miles
              </label>
              <input
                className={
                  (errors.proximityMiles
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Proximity Miles"
                {...register("proximityMiles", { required: true })}
              />
            </fieldset>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            onClick={handleSubmit(onSubmit)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.data.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-52"}>{e.days}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.spotRateWeight}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.directRateWeight}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.proximityMiles ?? 100}
              </td>

              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 dark:text-primary-8 text-light-1 cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
